import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Warning } from "@phosphor-icons/react";

export default function DeleteAccountDialog({ open, onOpenChange, account, accounts, onDeleted }) {
  const [target, setTarget] = useState("");
  const [deleting, setDeleting] = useState(false);
  const count = Number(account?.transaction_count || 0);
  const options = useMemo(
    () => (accounts || []).filter((item) => item.id !== account?.id),
    [accounts, account],
  );

  useEffect(() => {
    if (open) setTarget("");
  }, [open, account]);

  if (!account) return null;

  const selected = options.find((item) => item.id === target);
  const confirm = async () => {
    if (count && !target) return;
    setDeleting(true);
    try {
      const query = count ? `?reassign_to=${encodeURIComponent(target)}` : "";
      const response = await api.delete(`/accounts/${account.id}${query}`);
      const moved = Number(response.data?.moved || 0);
      toast.success(
        moved
          ? `“${account.name}” deleted. ${moved} transaction${moved === 1 ? "" : "s"} moved to “${response.data.moved_to}”.`
          : `“${account.name}” deleted`,
      );
      onOpenChange(false);
      onDeleted && onDeleted();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not delete account");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Delete “{account.name}”?</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {count
              ? `${count} transaction${count === 1 ? " is" : "s are"} linked to this account. Choose where to move ${count === 1 ? "it" : "them"}.`
              : "This account has no transactions and can be removed straight away."}
          </DialogDescription>
        </DialogHeader>

        {count > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="border border-border rounded-md px-3 py-2 flex-1 min-w-0 text-sm truncate">
                {account.name}
              </div>
              <ArrowRight size={16} className="shrink-0 text-muted-foreground" />
              <div className="border border-dashed border-border rounded-md px-3 py-2 flex-1 min-w-0 text-sm truncate">
                {selected?.name || "Pick an account…"}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Move transactions to</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="mt-1 bg-background" data-testid="account-reassign-select">
                  <SelectValue placeholder="Select another account" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {options.map((item) => (
                    <SelectItem
                      key={item.id}
                      value={item.id}
                      data-testid={`account-reassign-${item.id}`}
                    >
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {(account.type === "main" || account.type === "checking") && (
          <p className="text-xs text-muted-foreground flex gap-2 items-start">
            <Warning size={15} className="mt-0.5 shrink-0" />
            Another account will become your main account.
          </p>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="delete-account-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={deleting || (count > 0 && !target)}
            className="bg-expense text-white hover:bg-expense/90 font-semibold"
            data-testid="delete-account-confirm"
          >
            {deleting ? "Deleting…" : count ? "Move & delete" : "Delete account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
